/**
 * DatabeleidSection — Per-account databeleid (privacy-rol, rechten, quitclaims, bewaartermijnen).
 * Read-only weergave; bron-of-truth is Airtable (Accounts-tabel). Velden komen mee uit
 * /Api/klanten/:accountId (cellFormat=string). Defaults volgen het standaard FF-beleid.
 */
import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

const T = {
  primary: '#526075', primaryContainer: '#d5e3fd',
  surface: '#ffffff', surfaceLow: '#f1f4f5',
  text: '#2d3335', secondary: '#556071', outline: '#767c7e', outlineVariant: '#adb3b5',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
};

const Icon = ({ name, size = 24, sx = {} }) => (
  <span className="material-symbols-outlined" style={{ fontSize: size, fontVariationSettings: "'FILL' 0, 'wght' 400", ...sx }}>{name}</span>
);

// ── Helpers ──────────────────────────────────────────────────────────────────
const toMonths = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = parseInt(String(v).replace(/[^\d-]/g, ''), 10);
  return Number.isFinite(n) ? n : null;
};

const monthsToYears = (m) => {
  if (m == null) return '';
  const y = m / 12;
  return Number.isInteger(y) ? `${y} jaar` : `${y.toFixed(1).replace('.', ',')} jaar`;
};

// ── Section + group headers ───────────────────────────────────────────────────
function SectionLabel({ icon, text, tint = T.tertiaryContainer, color = T.tertiary }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
      <Box sx={{ width: 28, height: 28, borderRadius: '0.5rem', bgcolor: tint, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={icon} size={16} sx={{ color }} />
      </Box>
      <Typography sx={{ fontFamily: T.font, fontSize: '0.82rem', fontWeight: 700, color: T.text, letterSpacing: '-0.01em' }}>
        {text}
      </Typography>
    </Box>
  );
}

function FieldLabel({ icon, label, hint }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
      {icon && <Icon name={icon} size={15} sx={{ color: T.outlineVariant }} />}
      <Typography sx={{ fontFamily: T.font, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: T.outlineVariant }}>
        {label}
      </Typography>
      {hint && (
        <Tooltip title={hint} arrow placement="top">
          <Box sx={{ display: 'inline-flex', cursor: 'help' }}>
            <Icon name="help" size={13} sx={{ color: T.outlineVariant }} />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
}

function DefaultTag() {
  return (
    <Tooltip title="Niet expliciet ingesteld — standaard FF-beleid" arrow placement="top">
      <Box sx={{
        display: 'inline-flex', alignItems: 'center',
        fontFamily: T.fontBody, fontSize: '0.52rem', fontWeight: 800,
        textTransform: 'uppercase', letterSpacing: '0.06em',
        color: T.outline, bgcolor: T.surfaceLow,
        px: 0.6, py: 0.15, borderRadius: '4px', cursor: 'help',
      }}>
        standaard
      </Box>
    </Tooltip>
  );
}

const cardSx = {
  bgcolor: T.surface, borderRadius: '0.75rem', p: 2.25,
  border: '1px solid rgba(45,51,53,0.06)',
  display: 'flex', flexDirection: 'column',
};

// ── Party toggle (Full Frame ↔ Klant) ─────────────────────────────────────────
function PartyToggle({ value }) {
  const parties = ['Full Frame', 'Klant'];
  return (
    <Box sx={{ display: 'inline-flex', p: 0.4, borderRadius: '0.625rem', bgcolor: T.surfaceLow, gap: 0.4 }}>
      {parties.map((p) => {
        const active = (value || '').toLowerCase() === p.toLowerCase();
        return (
          <Box key={p} sx={{
            px: 1.75, py: 0.65, borderRadius: '0.45rem',
            fontFamily: T.fontBody, fontSize: '0.8rem', fontWeight: active ? 700 : 500,
            color: active ? '#fff' : T.outline,
            bgcolor: active ? T.primary : 'transparent',
            boxShadow: active ? '0 1px 4px rgba(82,96,117,0.35)' : 'none',
            transition: 'all 0.15s',
          }}>
            {p}
          </Box>
        );
      })}
    </Box>
  );
}

function PartyCard({ icon, label, hint, value, isDefault, desc }) {
  return (
    <Box sx={cardSx}>
      <FieldLabel icon={icon} label={label} hint={hint} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: desc ? 1.25 : 0 }}>
        <PartyToggle value={value} />
        {isDefault && <DefaultTag />}
      </Box>
      {desc && (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outline, lineHeight: 1.5 }}>
          {desc}
        </Typography>
      )}
    </Box>
  );
}

// ── Choice card (badge + implication) ─────────────────────────────────────────
function ChoiceCard({ icon, label, hint, value, isDefault, accent, desc, big }) {
  const color = accent?.color || T.primary;
  const bg = accent?.bg || T.primaryContainer;
  return (
    <Box sx={cardSx}>
      <FieldLabel icon={icon} label={label} hint={hint} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: desc ? 1.25 : 0 }}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75,
          px: 1.5, py: 0.6, borderRadius: '0.5rem',
          bgcolor: bg, color,
          fontFamily: T.fontBody, fontWeight: 700,
          fontSize: big ? '0.9rem' : '0.82rem',
        }}>
          {value || 'Niet ingesteld'}
        </Box>
        {isDefault && <DefaultTag />}
      </Box>
      {desc && (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outline, lineHeight: 1.5 }}>
          {desc}
        </Typography>
      )}
    </Box>
  );
}

// ── Retention timeline ─────────────────────────────────────────────────────────
function RetentionBar({ label, months, widthPct, isDefault, color }) {
  const has = months != null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ width: { xs: 130, sm: 180 }, flexShrink: 0 }}>
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.78rem', fontWeight: 600, color: T.text, lineHeight: 1.3 }}>
          {label}
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ height: 10, borderRadius: '999px', bgcolor: T.surfaceLow, overflow: 'hidden' }}>
          <Box sx={{
            height: '100%', width: has ? `${Math.max(widthPct, 4)}%` : '0%',
            background: `linear-gradient(90deg, ${color}cc, ${color})`,
            borderRadius: '999px', transition: 'width 0.4s ease',
          }} />
        </Box>
      </Box>
      <Box sx={{ width: 150, flexShrink: 0, textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75 }}>
        {has ? (
          <>
            <Typography sx={{ fontFamily: T.font, fontSize: '0.92rem', fontWeight: 800, color: T.text }}>
              {months} <Box component="span" sx={{ fontSize: '0.68rem', fontWeight: 600, color: T.outline }}>mnd</Box>
            </Typography>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outlineVariant }}>
              · {monthsToYears(months)}
            </Typography>
            {isDefault && <DefaultTag />}
          </>
        ) : (
          <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outlineVariant }}>—</Typography>
        )}
      </Box>
    </Box>
  );
}

function RetentionTimeline({ rows }) {
  const max = Math.max(...rows.map(r => r.months || 0), 1);
  return (
    <Box sx={{ ...cardSx, gap: 2.5, p: 2.75 }}>
      {rows.map((r) => (
        <RetentionBar
          key={r.label}
          label={r.label}
          months={r.months}
          widthPct={r.months != null ? (r.months / max) * 100 : 0}
          isDefault={r.isDefault}
          color={r.color}
        />
      ))}
      <Box sx={{ borderTop: '1px dashed rgba(45,51,53,0.1)', pt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Icon name="info" size={14} sx={{ color: T.outlineVariant }} />
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.outline, lineHeight: 1.5 }}>
          Bewaartermijnen gelden vanaf afronding van het project. Na afloop wordt het materiaal verwijderd volgens de gekozen verwijderprioriteit.
        </Typography>
      </Box>
    </Box>
  );
}

// ── Constants: defaults + implication copy ─────────────────────────────────────
const ROLE_INFO = {
  'Verwerkingsverantwoordelijke': 'Full Frame bepaalt doel en middelen van de verwerking en is onder de AVG primair verantwoordelijk voor de gegevens.',
  'Verwerker': 'Full Frame verwerkt gegevens in opdracht van en onder verantwoordelijkheid van de klant. Een verwerkersovereenkomst is vereist.',
};

const GEBIED_INFO = {
  'Wereldwijd': 'Gegevens mogen wereldwijd worden verwerkt en opgeslagen.',
  'EER': 'Verwerking en opslag blijven binnen de Europese Economische Ruimte (EER).',
};

const PRIORITY_META = {
  'Lage prioriteit': {
    accent: { color: '#10b981', bg: '#ecfdf5' }, icon: 'check_circle',
    desc: 'De bestanden vallen onder het standaardbeleid en mogen op de normale wijze worden verwijderd wanneer nodig.',
  },
  'Na klantcontact': {
    accent: { color: '#d97706', bg: '#fef3c7' }, icon: 'forum',
    desc: 'De klant wil meebeslissen welke projecten na de bewaartermijn worden verwijderd, met de optie om te verlengen.',
  },
  'Hoge prioriteit': {
    accent: { color: '#dc2626', bg: '#fef2f2' }, icon: 'priority_high',
    desc: 'De klant stelt strenge eisen aan de verwijdertermijn; projecten worden met beleid en zorgvuldig verwijderd.',
  },
};

// ── Main ───────────────────────────────────────────────────────────────────────
export default function DatabeleidSection({ beleid }) {
  const b = beleid || {};

  // Resolve value with documented standard fallback. Returns [value, isDefault].
  const resolve = (raw, std) => {
    if (raw !== null && raw !== undefined && String(raw).trim() !== '') return [String(raw).trim(), false];
    if (std !== null && std !== undefined) return [std, true];
    return [null, false];
  };

  const [rol, rolDef] = resolve(b.rolFullFrame, 'Verwerkingsverantwoordelijke');
  const [eigendom, eigendomDef] = resolve(b.eigendomsrecht, 'Full Frame');
  const [gebied, gebiedDef] = resolve(b.verwerkingsgebied, null);
  const [qcResp, qcRespDef] = resolve(b.quitclaimVerantwoordelijke, 'Full Frame');
  const [qcTpl, qcTplDef] = resolve(b.quitclaimGebruikVan, 'Full Frame');
  const [retVanuit, retVanuitDef] = resolve(b.retentieBepaaldVanuit, 'Full Frame');
  const [prio, prioDef] = resolve(b.prioriteitVerwijdering, 'Lage prioriteit');

  const ruwM = toMonths(b.retentieRuw);
  const mastersM = toMonths(b.retentieMasters);
  const compleetM = toMonths(b.retentieCompleet);
  const ruw = ruwM != null ? [ruwM, false] : [48, true];
  const masters = mastersM != null ? [mastersM, false] : [84, true];
  const compleet = compleetM != null ? [compleetM, false] : [60, true];

  const extra = (b.retentieExtra || '').trim();
  const prioMeta = PRIORITY_META[prio] || PRIORITY_META['Lage prioriteit'];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {/* Intro */}
      <Box>
        <SectionLabel icon="shield" text="Databeleid" />
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.8rem', color: T.outline, lineHeight: 1.6, mt: -0.5 }}>
          Privacy-rol, rechten en bewaartermijnen die voor dit account gelden. Deze velden worden beheerd in Airtable;
          ontbrekende waarden tonen het standaard Full Frame-beleid.
        </Typography>
      </Box>

      {/* GROEP 1 — Privacy & rechten */}
      <Box>
        <SectionLabel icon="gavel" text="Privacy & rechten" tint={T.primaryContainer} color={T.primary} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <ChoiceCard
            icon="account_balance"
            label="Rol van Full Frame"
            hint="Onder de privacy-wetgeving (AVG): wat is de rol van Full Frame bij projecten van dit account?"
            value={rol}
            isDefault={rolDef}
            accent={rol === 'Verwerker' ? { color: '#7c3aed', bg: '#ede9fe' } : { color: T.primary, bg: T.primaryContainer }}
            desc={ROLE_INFO[rol]}
            big
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
            <PartyCard
              icon="copyright"
              label="Eigendomsrecht"
              hint="Wie heeft tijdens het proces en na afronding de rechten op zowel het beeld/audiomateriaal als de eindproducties?"
              value={eigendom}
              isDefault={eigendomDef}
              desc="Wie bezit het beeld/audiomateriaal en de eindproducties — tijdens én na het project."
            />
            <ChoiceCard
              icon="public"
              label="Verwerkingsgebied beperkt tot"
              hint="Tot welk geografisch gebied zijn verwerkingen van gegevens van dit account beperkt?"
              value={gebied}
              isDefault={gebiedDef}
              accent={gebied === 'EER' ? { color: '#10b981', bg: '#ecfdf5' } : { color: T.secondary, bg: T.surfaceLow }}
              desc={GEBIED_INFO[gebied] || 'Nog geen verwerkingsgebied vastgelegd voor dit account.'}
            />
          </Box>
        </Box>
      </Box>

      {/* GROEP 2 — Quitclaims */}
      <Box>
        <SectionLabel icon="assignment_turned_in" text="Quitclaims" tint="#fef3c7" color="#d4943a" />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          <PartyCard
            icon="how_to_reg"
            label="Verantwoordelijke"
            hint="Wie is er verantwoordelijk voor de quitclaims bij het vergaren van beeld/audiomateriaal?"
            value={qcResp}
            isDefault={qcRespDef}
            desc="Regelt de quitclaims bij het vergaren van beeld- en audiomateriaal."
          />
          <PartyCard
            icon="description"
            label="Templates aangeleverd door"
            hint="Wie bouwt de quitclaims op en levert de templates aan?"
            value={qcTpl}
            isDefault={qcTplDef}
            desc="Bouwt de quitclaims op en levert de templates aan."
          />
        </Box>
      </Box>

      {/* GROEP 3 — Bewaartermijnen & verwijdering */}
      <Box>
        <SectionLabel icon="history" text="Bewaartermijnen & verwijdering" tint={T.tertiaryContainer} color={T.tertiary} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <PartyCard
            icon="rule"
            label="Beleid bepaald vanuit"
            hint="Vanuit wie komt het beleid rondom de bewaartermijnen?"
            value={retVanuit}
            isDefault={retVanuitDef}
            desc="Bepaalt de bewaartermijnen die hieronder gelden."
          />

          <RetentionTimeline
            rows={[
              { label: 'Ruw beeldmateriaal', months: ruw[0], isDefault: ruw[1], color: '#d4943a' },
              { label: 'Masters & quitclaims', months: masters[0], isDefault: masters[1], color: T.primary },
              { label: 'Compleet project', months: compleet[0], isDefault: compleet[1], color: T.tertiary },
            ]}
          />

          {/* Verwijderprioriteit */}
          <Box sx={{ ...cardSx, borderColor: `${prioMeta.accent.color}30` }}>
            <FieldLabel icon="auto_delete" label="Prioriteit verwijdering" hint="Welke prioriteitsgraad heeft verwijdering van de gegevens van dit account na afloop van de bewaartermijn?" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: '0.5rem', bgcolor: prioMeta.accent.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name={prioMeta.icon} size={20} sx={{ color: prioMeta.accent.color }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontFamily: T.font, fontSize: '0.92rem', fontWeight: 800, color: prioMeta.accent.color }}>
                    {prio}
                  </Typography>
                  {prioDef && <DefaultTag />}
                </Box>
                <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.74rem', color: T.outline, lineHeight: 1.5, mt: 0.25 }}>
                  {prioMeta.desc}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Extra informatie */}
          {extra && (
            <Box sx={cardSx}>
              <FieldLabel icon="sticky_note_2" label="Extra informatie" />
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.82rem', color: T.text, lineHeight: 1.65, whiteSpace: 'pre-line' }}>
                {extra.replace(/\*\*/g, '')}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
